import {FC, useState} from "react";
import {Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table} from "reactstrap";
import {getTournamentSysAdminApi} from "../../../../System/systemConfig";
import {useMutation, useQuery} from "@tanstack/react-query";
import {Section} from "../../../../common/Section";
import {faArrowDown, faArrowUp, faTrash} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

interface Props {
    tournamentId: string;
    onClose: () => void;
}

export const TournamentUpdaterEdit: FC<Props> = (props) => {

    const [addResultUpdaterName, setAddResultUpdaterName] = useState<string>('');
    const [addScheduleUpdaterName, setAddScheduleUpdaterName] = useState<string>('');

    const api = getTournamentSysAdminApi();

    const tournamentUpdaterQuery = useQuery({
        queryKey: ['tournamentUpdater.get', props.tournamentId],
        queryFn: () => api.getTournamentUpdaterByTournamentId({tId: props.tournamentId}),
    })

    const availableUpdatersQuery = useQuery({
        queryKey: ['tournamentUpdater.available'],
        queryFn: () => api.getAvailableTournamentUpdater({tId: props.tournamentId}),
    });

    const addUpdaterMutation = useMutation({
        mutationFn: (p: { updaterName: string, type: string }) => api.addTournamentUpdater({
            tId: props.tournamentId,
            name: p.updaterName,
            type: p.type
        }),
        onSuccess: () => {
            tournamentUpdaterQuery.refetch();
            availableUpdatersQuery.refetch();
        }
    });

    const deleteUpdaterMutation = useMutation({
        mutationFn: (p: { updaterName: string, type: string }) => api.removeTournamentUpdater({
            tId: props.tournamentId,
            name: p.updaterName,
            type: p.type
        }),
        onSuccess: () => {
            tournamentUpdaterQuery.refetch();
            availableUpdatersQuery.refetch();
        }
    });

    const orderUpUpdaterMutation = useMutation({
        mutationFn: (p: { updaterName: string, type: string }) => api.orderUpTournamentUpdater({
            tId: props.tournamentId,
            name: p.updaterName,
            type: p.type
        }),
        onSuccess: () => {
            tournamentUpdaterQuery.refetch();
            availableUpdatersQuery.refetch();
        }
    });

    const orderDownUpdaterMutation = useMutation({
        mutationFn: (p: { updaterName: string, type: string }) => api.orderDownTournamentUpdater({
            tId: props.tournamentId,
            name: p.updaterName,
            type: p.type
        }),
        onSuccess: () => {
            tournamentUpdaterQuery.refetch();
            availableUpdatersQuery.refetch();
        }
    });

    const getScheduleUpdaters = () => {
        return tournamentUpdaterQuery.data?.filter(tu => tu.type === 'schedule') ?? [];
    }

    const getResultUpdaters = () => {
        return tournamentUpdaterQuery.data?.filter(tu => tu.type === 'result') ?? [];
    }

    const getAvailableResultUpdaters = () => {
        return availableUpdatersQuery.data?.filter(tu => tu.type === 'result' &&
            !getResultUpdaters().some(ex => tu.updaterName === ex.updaterName)) ?? [];
    }

    const getAvailableScheduleUpdaters = () => {
        return availableUpdatersQuery.data?.filter(tu => tu.type === 'schedule' &&
            !getScheduleUpdaters().some(ex => tu.updaterName === ex.updaterName)) ?? [];
    }

    const onAddUpdater = (updaterName: string, type: 'schedule' | 'result') => {
        addUpdaterMutation.mutate({updaterName, type});
    }

    const onRemoveUpdater = (updaterName: string, type: 'schedule' | 'result') => {
        deleteUpdaterMutation.mutate({updaterName, type});
    }

    const onOrderUpUpdater = (updaterName: string, type: 'schedule' | 'result') => {
        orderUpUpdaterMutation.mutate({updaterName, type});
    }

    const onOrderDownUpdater = (updaterName: string, type: 'schedule' | 'result') => {
        orderDownUpdaterMutation.mutate({updaterName, type});
    }

    return (
        <Modal isOpen={true} toggle={props.onClose} className="modal-lg">
            <ModalHeader>Tournament Updater Edit</ModalHeader>
            <ModalBody>
                <Section>
                    <h5>Schedule Updaters</h5>
                    <Table>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {getScheduleUpdaters().map((updater) => (
                            <tr key={updater.id}>
                                <td>{updater.updaterName}</td>
                                <td className="d-flex gap-2">
                                    <button onClick={() => onRemoveUpdater(updater.updaterName, 'schedule')}
                                            className="border-0">
                                        <FontAwesomeIcon icon={faTrash} size="sm" color="red" className="border-0"/>
                                    </button>
                                    <button onClick={() => onOrderDownUpdater(updater.updaterName, 'schedule')}
                                            className="border-0">
                                        <FontAwesomeIcon icon={faArrowDown} size="sm" className="border-0"/>
                                    </button>
                                    <button onClick={() => onOrderUpUpdater(updater.updaterName, 'schedule')}
                                            className="border-0">
                                        <FontAwesomeIcon icon={faArrowUp} size="sm" className="border-0"/>
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                    <FormGroup>
                        <Label>Add Schedule Updater</Label>
                        <div className="d-flex flex-row gap-2">
                            <Input type="select"
                                   value={addScheduleUpdaterName}
                                   onChange={(e) => setAddScheduleUpdaterName(e.target.value)}>
                                <option value="">Select Schedule Updater</option>
                                {getAvailableScheduleUpdaters().map((updater) =>
                                    <option key={updater.updaterName}
                                            value={updater.updaterName}>{updater.updaterName}</option>
                                )}
                            </Input>
                            <Button type="submit"
                                    color="success"
                                    disabled={addScheduleUpdaterName === ''}
                                    onClick={() => onAddUpdater(addScheduleUpdaterName, 'schedule')}>Add</Button>
                        </div>
                    </FormGroup>
                </Section>

                <Section>
                    <h5>Result Updaters</h5>
                    <Table>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {getResultUpdaters().map((updater) => (
                            <tr key={updater.id}>
                                <td>{updater.updaterName}</td>
                                <td className="d-flex gap-2">
                                    <button onClick={() => onRemoveUpdater(updater.updaterName, 'result')}
                                            className="border-0">
                                        <FontAwesomeIcon icon={faTrash} size="sm" color="red" className="border-0"/>
                                    </button>
                                    <button onClick={() => onOrderDownUpdater(updater.updaterName, 'result')}
                                            className="border-0">
                                        <FontAwesomeIcon icon={faArrowDown} size="sm" className="border-0"/>
                                    </button>
                                    <button onClick={() => onOrderUpUpdater(updater.updaterName, 'result')}
                                            className="border-0">
                                        <FontAwesomeIcon icon={faArrowUp} size="sm" className="border-0"/>
                                    </button>
                                </td>
                            </tr>))}
                        </tbody>
                    </Table>
                    <FormGroup>
                        <Label>Add Result Updater</Label>
                        <div className="d-flex flex-row gap-2">
                            <Input type="select"
                                   value={addResultUpdaterName}
                                   onChange={(e) => setAddResultUpdaterName(e.target.value)}>
                                <option value="">Select Result Updater</option>
                                {getAvailableResultUpdaters().map((updater) =>
                                    <option key={updater.updaterName}
                                            value={updater.updaterName}>{updater.updaterName}</option>
                                )}
                            </Input>
                            <Button type="submit"
                                    color="success"
                                    disabled={addResultUpdaterName === ''}
                                    onClick={() => onAddUpdater(addResultUpdaterName, 'result')}>Add</Button>
                        </div>
                    </FormGroup>
                </Section>

            </ModalBody>
            <ModalFooter>
                <Button color="success" onClick={props.onClose}>Close</Button>
            </ModalFooter>

        </Modal>
    );
}